import React, { useState } from "react";
import { ColorPicker } from "primereact/colorpicker";
import BoardData from "../boards/boardData";

import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, horizontalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableColumn = ({ id, statusName, itemCount, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div className="card-board" style={style}>
      <div className="card-board-header" ref={setNodeRef} {...attributes} {...listeners}>
        <h5 className="header-title">
          <i className={"ri-contrast-line"}></i> {statusName}
        </h5>
        <span>{itemCount}</span>
        <button className="btn">
          <i className="ri-add-line"></i>
        </button>
      </div>
      {children}
    </div>
  );
};

function TaskBoards(props) {
  const { taskList } = props;
  const [color, setColor] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);

  // Build columns state from taskList once
  const [columns, setColumns] = useState(() => {
    const cols = {};

    if (taskList?.statusList) {
      taskList.statusList.forEach((status) => {
        const columnId = status.task_status.replace(/\s+/g, "").toLowerCase();

        cols[columnId] = {
          name: status.task_status,
          items: taskList[columnId]?.data || [],
        };
      });
    }

    return cols;
  });

  // Maintain visual order of the columns for dnd-kit
  const [columnOrder, setColumnOrder] = useState(() => {
    if (!taskList?.statusList) return [];
    return taskList.statusList.map((status) => status.task_status.replace(/\s+/g, "").toLowerCase());
  });

  // Called from BoardData when dragging a task starts
  const handleDragStart = (columnId, item) => {
    setDraggedItem({ columnId, item });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetColumnId) => {
    e.preventDefault();
    if (!draggedItem) return;

    const { columnId: sourceColumnId, item } = draggedItem;

    // safety
    if (!columns[sourceColumnId] || !columns[targetColumnId]) return;
    if (sourceColumnId === targetColumnId) return;

    setColumns((prev) => {
      const next = { ...prev };

      // remove from source column
      next[sourceColumnId] = {
        ...next[sourceColumnId],
        items: next[sourceColumnId].items.filter((i) => i.id !== item.id),
      };

      // add to target column
      next[targetColumnId] = {
        ...next[targetColumnId],
        items: [...next[targetColumnId].items, item],
      };

      return next;
    });

    setDraggedItem(null);
  };

  // Handle column reordering using dnd-kit
  const handleColumnDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setColumnOrder((prevOrder) => {
      const oldIndex = prevOrder.indexOf(active.id);
      const newIndex = prevOrder.indexOf(over.id);

      if (oldIndex === -1 || newIndex === -1) return prevOrder;

      return arrayMove(prevOrder, oldIndex, newIndex);
    });
  };

  return (
    <div className="board-grid">
      <DndContext collisionDetection={closestCenter} onDragEnd={handleColumnDragEnd}>
        {columnOrder.length > 0 && (
          <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
            {columnOrder.map((columnId) => {
              const column = columns[columnId] || { items: [] };

              // Find the status object for label (fallback to columnId if not found)
              const status = taskList.statusList?.find((s) => s.task_status.replace(/\s+/g, "").toLowerCase() === columnId);

              const statusName = status?.task_status || columnId;

              return (
                <SortableColumn key={columnId} id={columnId} statusName={statusName} itemCount={column.items.length}>
                  <div className="card-board-body" onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, columnId)}>
                    {column.items.map((taskItem) => (
                      <BoardData key={taskItem.id} item={taskItem} handleDragStart={handleDragStart} columnId={columnId} />
                    ))}

                    <button className="btn board-task-add-btn">
                      <i className="ri-add-line"></i> Add Task
                    </button>
                  </div>
                </SortableColumn>
              );
            })}
          </SortableContext>
        )}
      </DndContext>

      {/* Add group dropdown */}
      <div className="add-board-group">
        <button className="btn add-group-btn" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
          <i className="ri-add-line"></i> Add group
        </button>
        <div className="dropdown task-dropdown">
          <div className="dropdown-menu">
            <form className="dropdown-item">
              <ColorPicker value={color} onChange={(e) => setColor(e.value)} />
              <input type="text" className="form-control" placeholder="STATUS NAME" />
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TaskBoards;
